import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { DatePickerPresets, GlobalFunctions, SnackType } from '@global/GlobalFunctions';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { RuleEdit } from '@services/rules-data.service';
import { combineLatest, takeUntil, distinctUntilChanged, filter, Subject, finalize, tap } from 'rxjs';
import { AssetDataPoint, DataPoint, ISensorData } from '@services/datapoints-data.service';
import { ChartComponent, ISelectionCompleteEventArgs, StripLineSettingsModel } from '@syncfusion/ej2-angular-charts';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { Browser } from '@syncfusion/ej2-base';
import { convertEpochToDateString } from '@functions/functions';
import { TestDistributionComponent } from '../test-distribution/test-distribution.component';
import { AssetDistribution, ModelsService } from '@services/models.service';
import { MultiSelect } from '@syncfusion/ej2-angular-dropdowns';

export class CreateDistributionData {
	asset?: LevelAsset;
	viewing?: boolean;
	editing?: boolean;
	distributionID?: number;
	existingDistribution?: AssetDistribution;
}

@Component({
	selector: 'app-create-asset-distribution',
	templateUrl: './create-asset-distribution.component.html',
	styleUrls: ['./create-asset-distribution.component.scss']
})
export class CreateAssetDistributionComponent implements OnInit, OnDestroy {

	/** Form Group */
	dialogForm: FormGroup;

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** Chart Legend */
	public legend: Object;
	/** Sensor Data Label */
	public dataLabels: Object;
	/** Tooltip Settings */
	public tooltipSettings: Object;
	/** Chart boarder Settings */
	public borderSettings: Object;

	/** crosshair */
	public crosshair: object = {
		enable: true,
		lineType: 'Vertical'
	};

	// /** Define the Time Selection Buttons */
	// public chipDurations = [
	// 	// { time: "00:00:30", text: "30m", toolTip: "30 Minutes" },
	// 	// { time: "00:01:00", text: "1h", toolTip: "1 Hour" },
	// 	// { time: "00:02:00", text: "2h", toolTip: "2 Hours" },
	// 	// { time: "00:04:00", text: "4h", toolTip: "4 Hours" },
	// 	{ time: "00:08:00", text: "8h", toolTip: "8 Hours" },
	// 	{ time: "00:12:00", text: "12h", toolTip: "12 Hours" },
	// 	{ time: "01:00:00", text: "1d", toolTip: "1 Day" },
	// 	{ time: "02:00:00", text: "2d", toolTip: "2 Days" },
	// 	{ time: "07:00:00", text: "7d", toolTip: "7 Days" },
	// 	{ time: "30:00:00", text: "30d", toolTip: "30 Days" },
	// 	{ time: "60:00:00", text: "60d", toolTip: "60 Days" },
	// 	{ time: "90:00:00", text: "90d", toolTip: "90 Days" }
	// ];

	/** maxMonths */
	private maxMonths: number = 36; // max months increased as retrieving data for long periods no longer an issue

	/** strDuration */
	public strDuration: string = ''; //'00:12:00';
	/** Define start date */
	public startDate: Date;
	/** Define end date */
	public endDate: Date;
	/** paramStartDate */
	public paramStartDate: Date;
	/** paramEndDate */
	public paramEndDate: Date;

	/** Spinner */
	public spinner: boolean

	public ruleEdits: RuleEdit[] = [];

	private seriesColors: { datapointID: number, color: string }[] = [];

	/** My Chart */
	@ViewChild('myDistChart') public myChart: ChartComponent;

	/** parsedData */
	public parsedData: ISensorData[] = [];

	/** used for displaying min and max for datapoints */
	public uniqueDPs: any[];

	// Define colors as private properties of the class
	private lightGreenCyan: string = "#00E1B3";         // Lighter green/cyan color
	private lightGreenCyanDarker1: string = "#02735c";  // Noticeably darker version of light green/cyan
	private lightGreenCyanDarker2: string = "#005244";  // Even darker version of light green/cyan

	private darkBlue: string = "#3366FF";               // Slightly lighter dark blue color
	private darkBlueDarker1: string = "#022073";        // Darker version of dark blue
	private darkBlueDarker2: string = "#010b2b";        // Even darker version of dark blue

	// Predefined array of default colors
	private defaultColors: string[] = [
		"#FF5733", // Red
		"#33FF57", // Green
		"#3357FF", // Blue
		"#F0E130", // Yellow
		"#FF33F0", // Pink
		"#33FFF6", // Aqua
		"#FF9833", // Orange
		"#F633FF", // Purple
		"#8BFF33"  // Light Green
	];

	public primaryMax: number = 0;
	public secondaryMax: number = 0;

	// Variable to track the next default color to use
	private defaultColorIndex: number = 0;

	/** Scroll Position */
	private scrollPosition: any = [0, 0]

	/** shortMonths */
	private shortMonths: string[] = [
		"Jan", "Feb", "Mar", "Apr", "May", "Jun",
		"Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
	];

	/** Data Point Values */
	public dataPointValues: object[];

	/**
	 * Chart Selection mode
	 * None|Point|Series|Cluster|DragXY|DragX|DragY
	 */
	public selectionMode: string;

	/** Selected AccountUID */
	public selectedAccountUID: string;

	/** Selected Organisation ID */
	public selectedOrganisationUID: string;

	/** Chart Zoom Settings */
	public zoomSettings: Object;

	/** asset */
	public asset: LevelAsset;

	/** Data Points */
	public dataPoints: AssetDataPoint[];

	/** Filtered Data Points */
	public filteredDataPoints: AssetDataPoint[] = [];

	/** Sensor X Axis */
	public sensorXAxis: Object;
	/** Sensor Y Axis */
	public sensoryAxis: object;

	/** Sensor pen Pallet Colours */
	public palette: object;

	/** Chart Text Style */
	public titleStyle: Object;

	public visibleStartDate: Date;
	public visibleEndDate: Date;

	public automaticSelected: boolean = false;
	public remindSelected: boolean = false;

	/** selectedDistributionData */
	public selectedDistributionData: any[] = [];

	/** onlyDisplayCharted */
	public onlyDisplayCharted: boolean = true;

	/** automaticData */
	public automaticData: any[] = [];

	/** noOkay */
	public noOkay: boolean = false;

	/** existingDistribution */
	public existingDistribution: AssetDistribution;

	/** viewing */
	public viewing: boolean = false;

	/** existingDistValues */
	public existingDistValues: ISensorData[] = [];

	/** selectedData */
	public selectedData: { series: number, point: number }[] = [];

	/** testedDistributionImage */
	public testedDistributionImage: string = "";

	/** manualSelected */
	public manualSelected: boolean = true;

	/** editing */
	public editing: boolean = false;

	/** datapointFields */
	public datapointFields: Object = { text: 'DPTitle', value: 'DataPointID' };

	/** datapointBox */
	@ViewChild('datapointBox') datapointBox: MultiSelect;

	/** firstRun */
	public firstRun: boolean = true;

	/** today - #71 */
	public today: Date = new Date();

	/** datePickerPresets - #71 */
	public datePickerPresets = DatePickerPresets;

	/**
	 * 
	 * @param fb 
	 * @param dialogRef 
	 * @param globalFunctions 
	 * @param assetsDataService 
	 * @param modelsService 
	 * @param siteSubscriptions 
	 * @param authenticationService 
	 * @param dialog 
	 * @param dialogData 
	 */
	constructor(
		private fb: FormBuilder,
		public dialogRef: MatDialogRef<CreateAssetDistributionComponent>,
		private globalFunctions: GlobalFunctions,
		private assetsDataService: AssetsDataService,
		private modelsService: ModelsService,
		private siteSubscriptions: siteSubscriptions,
		public dialog: MatDialog,
		@Inject(MAT_DIALOG_DATA) public dialogData: CreateDistributionData,
	) {
		combineLatest([
			this.siteSubscriptions.selectedAccountGUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			filter(([selectedAccountGUID, selectedOrganisationUID]) =>
				!!selectedAccountGUID && !!selectedOrganisationUID
			)
		).subscribe({
			next: ([selectedAccountGUID, selectedOrganisationUID]) => {
				this.selectedAccountUID = selectedAccountGUID;
				this.selectedOrganisationUID = selectedOrganisationUID;

				if (this.dialogData.asset) {
					this.asset = this.dialogData.asset;
					if (this.dialogData.viewing) {
						this.viewing = true;
						this.strDuration = "custom";
						if (this.dialogData.existingDistribution) {
							this.existingDistribution = this.dialogData.existingDistribution;
						} else if (this.dialogData.distributionID) {
							this.getDistribution(this.dialogData.distributionID);
						}
					} else if (this.dialogData.existingDistribution && this.dialogData.editing) {
						this.editing = true;
						this.strDuration = "custom";
						this.existingDistribution = this.dialogData.existingDistribution;
					}
				}

				// this.zoomSettings = {
				// 	mode: 'X',
				// 	// enableMouseWheelZooming: true,
				// 	enablePinchZooming: true,
				// 	enableSelectionZooming: false,
				// 	enableScrollbar: true
				// };
				this.legend = { visible: false };
				this.tooltipSettings = {
					enable: true,
					//shared: true // Enables shared tooltip
				};
				this.selectionMode = 'DragX';
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: combined route parameters", false);
			}
		});
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
		this.defaultColorIndex = 0;

		this.seriesColors = [];

		if (this.strDuration === "") {
			this.strDuration = "00:12:00";
		}

		if (!this.viewing && !this.existingDistribution) {
			let parts: any[] = this.strDuration.split(":");
			this.endDate = new Date();
			this.startDate = new Date();
			this.startDate.setDate(this.startDate.getDate() - parts[0]);
			this.startDate.setHours(this.startDate.getHours() - parts[1]);
			this.startDate.setMinutes(this.startDate.getMinutes() - parts[2]);
		} else {
			if (this.existingDistribution) {
				this.startDate = new Date(this.existingDistribution.StartPoint - (1 * 24 * 60 * 60 * 1000));
				this.endDate = new Date(this.existingDistribution.EndPoint + (1 * 24 * 60 * 60 * 1000));
				this.manualSelected = !this.existingDistribution.IsAutomatic;
			}
		}

		this.visibleStartDate = this.startDate;
		this.visibleEndDate = this.endDate;

		this.setXAxis();
		this.GetDataPoints(this.asset)

		if (!this.viewing && !this.editing) {
			this.dialogForm = this.fb.group({
				isAutomatic: [false],
				automaticWindowWidth: [{ value: 14, disabled: true }],
				recreationFrequency: [{ value: 7, disabled: true }],
				overlapPeriod: [{ value: 0, disabled: true }],
				distributionData: [''],
				remind: [false],
				remindAfter: [{ value: '', disabled: true }],
				tolerance: [1],
				sensitivity: [50],
				datapoints: [{ value: '', disabled: true }]
			});
		} else if (this.editing && this.existingDistribution) {
			this.dialogForm = this.fb.group({
				isAutomatic: [{ value: this.existingDistribution.IsAutomatic, disabled: true }],
				automaticWindowWidth: [{ value: this.existingDistribution.IsAutomatic ? this.existingDistribution.AutomaticWindowWidth : 7, disabled: true }],
				recreationFrequency: [{ value: this.existingDistribution.RecreationFrequency, disabled: false }],
				overlapPeriod: [{ value: this.existingDistribution.OverlapPeriod, disabled: false }],
				distributionData: [''],
				remind: [{ value: false, disabled: true }],
				remindAfter: [{ value: '', disabled: true }],
				tolerance: [{ value: this.existingDistribution.Tolerance, disabled: false }],
				sensitivity: [{ value: this.existingDistribution.Sensitivity != 0 ? this.existingDistribution.Sensitivity : 50.00, disabled: false }],
				datapoints: [{ value: this.existingDistribution.DatapointIDs.split(","), disabled: true }]
			});
		} else {
			while (!this.existingDistribution) {
				// do nothing
			}
			this.dialogForm = this.fb.group({
				isAutomatic: [{ value: this.existingDistribution.IsAutomatic, disabled: true }],
				automaticWindowWidth: [{ value: this.existingDistribution.IsAutomatic ? this.existingDistribution.AutomaticWindowWidth : 7, disabled: true }],
				recreationFrequency: [{ value: this.existingDistribution.RecreationFrequency, disabled: true }],
				overlapPeriod: [{ value: this.existingDistribution.OverlapPeriod, disabled: true }],
				distributionData: [''],
				remind: [{ value: false, disabled: true }],
				remindAfter: [{ value: '', disabled: true }],
				tolerance: [{ value: this.existingDistribution.Tolerance, disabled: true }],
				sensitivity: [{ value: this.existingDistribution.Sensitivity != 0 ? this.existingDistribution.Sensitivity : 50.00, disabled: true }],
				datapoints: [{ value: this.existingDistribution.DatapointIDs.split(","), disabled: true }]
			});
		}
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		if (this.dialogForm.dirty) {
			this.globalFunctions.UserConfirm$('Lose any unsaved changes?').subscribe(OKResult => {
				if (OKResult) { this.dialogRef.close(); }
				// do nothing if result is false
			});
		} else {
			this.dialogRef.close();
		}
	}

	chartMouseDown(args: any) {
		// console.log("chartMouseDown")
		const scrollableDiv = document.getElementById('dockContent');
		const scrollX = scrollableDiv?.scrollLeft || 0;
		const scrollY = scrollableDiv?.scrollTop || 0;
		this.scrollPosition = [scrollX, scrollY]
	}

	chartresized(event: any) {
		// console.log(event);
	}

	private isZooming: boolean

	zoomComplete(args?: ISelectionCompleteEventArgs) {

		if (!this.isZooming) {
			this.isZooming = true;
			// console.log('Zooming completed', args);
			// Perform additional actions here

			// Reset the flag after some delay
			setTimeout(() => {
				this.isZooming = false;
				const scrollableDiv = document.getElementById('dockContent');

				// Restore the scroll position of the overflow div
				if (scrollableDiv) {
					scrollableDiv.scrollLeft = this.scrollPosition[0];
					scrollableDiv.scrollTop = this.scrollPosition[1];
				}
			}, 1); // Adjust the delay as needed
		}
	}

	/**
	 * onScroll
	 * @param event
	 */
	onScroll(event: any) {
		if (event.axis.orientation != 'Vertical') {
			if (event.zoomFactor != 1) {
				this.visibleStartDate = new Date(event.range.min);
				this.visibleEndDate = new Date(event.range.max);
			} else {
				this.visibleStartDate = this.startDate;
				this.visibleEndDate = this.endDate;
			}
		}
	}

	checkClick(event: any) {
		if (event.target.toLowerCase().includes("zooming_reset")) {
			this.zoomComplete();
			this.visibleStartDate = this.startDate;
			this.visibleEndDate = this.endDate;
		}
	}

	/**
	 * onZoom
	 * @param event
	 */
	onZoom(event: any) {
		if (event.axis.orientation != 'Vertical') {
			if (event.currentZoomFactor != 1) {
				this.visibleStartDate = new Date(event.currentVisibleRange.min);
				this.visibleEndDate = new Date(event.currentVisibleRange.max);
			} else {
				this.visibleStartDate = this.startDate;
				this.visibleEndDate = this.endDate;
			}
		}
	}

	/**
	 * onTooltipRender
	 * @param args args
	 */
	onTooltipRender(args: any): void {
		let timestamp: Date = args.data.pointX;
		const day = timestamp.getDate();
		const monthIndex = timestamp.getMonth();
		const minutes = timestamp.getMinutes();
		const hours = timestamp.getHours();
		const seconds = timestamp.getSeconds();
		const formattedDate = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')} ${day} ${this.shortMonths[monthIndex]}`;
		args.text = `${formattedDate} :${args.text.split(":").pop()}`;
	}

	/**
	 * chartHover
	 * @param event event
	 */
	chartHover(event: any) {
		if (this.myChart.primaryXAxis.stripLines) {
			let striplines: StripLineSettingsModel[] = this.myChart.primaryXAxis.stripLines;
			/** If we have hovered over any event stripline */
			if (event.target.includes("stripline_Over_rect_primaryXAxis")) {
				let hoverTime: number = event.axisData["primaryXAxis"];
				let targetStripline: StripLineSettingsModel | undefined;
				striplines.forEach(stripline => {
					if (<number>stripline.start <= hoverTime && <number>stripline.end >= hoverTime) {
						targetStripline = stripline;
					}
				});
				const scrollableDiv = document.getElementById('dockContent');
				const scrollX = scrollableDiv?.scrollLeft || 0;
				const scrollY = scrollableDiv?.scrollTop || 0;

				if (targetStripline && targetStripline.textStyle) {
					targetStripline.textStyle.color = 'black';
				}

				setTimeout(() => {
					if (scrollableDiv) {
						scrollableDiv.scrollLeft = scrollX;
						scrollableDiv.scrollTop = scrollY;
					}
				}, 1);
			} else {
				striplines.forEach(stripline => {
					const scrollableDiv = document.getElementById('dockContent');
					const scrollX = scrollableDiv?.scrollLeft || 0;
					const scrollY = scrollableDiv?.scrollTop || 0;

					if (stripline.textStyle) {
						stripline.textStyle.color = "transparent";
					}

					setTimeout(() => {
						if (scrollableDiv) {
							scrollableDiv.scrollLeft = scrollX;
							scrollableDiv.scrollTop = scrollY;
						}
					}, 1);
				})
			}
		}
	}

	/** navigateByDuration */
	navigateByDuration(isForward: boolean) {
		let duration: string = "";
		let start: number = 0;
		let end: number = 0;
		if (this.strDuration) {
			duration = this.strDuration;
		}

		if (duration == "custom") {
			let difference: number = this.endDate.getTime() - this.startDate.getTime();
			if (!isForward) {
				end = this.startDate.getTime();
				start = end - difference;
				this.startDate = new Date(Number(start));
				this.endDate = new Date(Number(end));
				this.GetDataPointValues(this.asset, start, end);
			} else {
				start = this.endDate.getTime();
				end = start + difference;
				if (end > Date.now()) {
					end = Date.now();
					start = end - difference;
				}
				this.startDate = new Date(Number(start));
				this.endDate = new Date(Number(end));
				this.GetDataPointValues(this.asset, start, end);
			}

		} else {
			let difference: number = this.timeSpanToMilliseconds(duration);
			if (!isForward) {
				end = this.startDate.getTime();
				start = end - difference;
				this.startDate = new Date(Number(start));
				this.endDate = new Date(Number(end));
				this.GetDataPointValues(this.asset, start, end);
			} else {
				start = this.endDate.getTime();
				end = start + difference;
				if (end > Date.now()) {
					end = Date.now();
					start = end - difference;
				}
				this.startDate = new Date(Number(start));
				this.endDate = new Date(Number(end));
				this.GetDataPointValues(this.asset, start, end);
			}
		}
		this.visibleStartDate = this.startDate;
		this.visibleEndDate = this.endDate;
	}

	/** timeSpanToMilliseconds */
	timeSpanToMilliseconds(timeSpan: string): number {
		// Split the string into components
		const [days, hours, minutes] = timeSpan.split(':').map(Number);

		// Validate the input
		if (isNaN(days) || isNaN(hours) || isNaN(minutes)) {
			throw new Error("Invalid time span format");
		}

		// Calculate the total milliseconds
		const milliseconds =
			(days * 24 * 60 * 60 * 1000) + // Days to milliseconds
			(hours * 60 * 60 * 1000) +     // Hours to milliseconds
			(minutes * 60 * 1000);         // Minutes to milliseconds

		return milliseconds;
	}

	/** getShortDate */
	getShortDate(date: Date): string {
		let result: string = "";
		result += date.getDate();
		result +=
			" " +
			this.shortMonths[date.getMonth()] +
			" " +
			date.getHours().toString().padStart(2, "0") +
			":" +
			date.getMinutes().toString().padStart(2, "0");

		return result;
	}

	/**
	 * selectTime
	 */
	selectTime(sDuration: string) {
		this.strDuration = sDuration

		let parts: any[] = this.strDuration.split(":")
		this.endDate = new Date()
		this.startDate = new Date()
		this.startDate.setDate(this.startDate.getDate() - parts[0]);
		this.startDate.setHours(this.startDate.getHours() - parts[1]);
		this.startDate.setMinutes(this.startDate.getMinutes() - parts[2]);

		this.visibleStartDate = this.startDate;
		this.visibleEndDate = this.endDate;

		this.setXAxis();
		this.GetDataPointValues(this.asset, this.startDate.getTime(), this.endDate.getTime())
	}

	/**
	 * Add a section to the graph
	 * @param lines stripLine Definition
	 */
	setXAxis(lines: object[] = []): void {

		/* Sensor Graph specific variables */
		this.sensorXAxis = {
			// title: 'Date',
			valueType: "DateTime", //'Category',
			intervalType: parseInt(this.strDuration.split(":")[0]) > 2 || this.strDuration == 'custom' ? "Days" : "Hours",
			// intervalType: "Hours",
			// interval: 1,
			labelFormat: parseInt(this.strDuration.split(":")[0]) > 2 || this.strDuration == 'custom' ? "dd-MMM" : "HH:mm dd-MMM",
			// labelFormat: "HH:mm:ss dd-MMM",
			// labelIntersectAction: "Rotate45",
			// labelRotation: 35,
			// labelRotation: 90,
			labelRotation: Browser.isDevice ? -45 : 0,
			majorTickLines: { visible: false },
			minorTickLines: { visible: false },
			majorGridLines: { visible: false },
			minorGridLines: { visible: false },
			// stripLines: lines

		};
	}

	/**
	 * DatePickerChange
	 * @param event
	 */
	DatePickerChange(event: any) {
		try {
			// Validate the Date Range < 6 Months
			// Validate the Date Range < 6 Months
			const date1 = new Date(event.startDate.getTime());
			const date2 = new Date(event.endDate.getTime());

			const diffInMs = Math.abs(date2.getTime() - date1.getTime());
			const diffInMonths = diffInMs / (1000 * 60 * 60 * 24 * 30.44);

			if (diffInMonths < this.maxMonths) {
				// console.log('The difference is less than ' + this.maxMonths + ' months');

				this.startDate = event.startDate;
				this.endDate = event.endDate
				/** Add a day to retrieve data for last day too */
				this.endDate.setDate(this.endDate.getDate() + 1)

				/** Reset Duration Search */
				this.strDuration = "custom";

				this.visibleStartDate = this.startDate;
				this.visibleEndDate = this.endDate;

				this.setXAxis();
				this.GetDataPointValues(this.asset, this.startDate.getTime(), this.endDate.getTime())
			}
		}
		catch (error) { this.globalFunctions.HandleError(error, "DatePickerChange Error: Unknown", false) }
	}

	/**
	 * Get Values for the DataPoints
	 * @param asset
	 * @param dateStart
	 * @param dateEnd
	 * @returns
	 */
	async GetDataPointValues(asset: LevelAsset, dateStart: any, dateEnd: any, returnValues?: boolean): Promise<any> {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		if (this.myChart) {
			this.myChart.primaryXAxis.zoomFactor = 1;
			this.myChart.primaryXAxis.zoomPosition = 0;
			this.myChart.clearSeries();
		}

		this.displaySpinner(true);

		// this.seriesColors = [];

		const scrollableDiv = document.getElementById('dockContent');
		const scrollX = scrollableDiv?.scrollLeft || 0;
		const scrollY = scrollableDiv?.scrollTop || 0;

		return new Promise<any>((resolve, reject) => {
			this.assetsDataService.getAssetDataPointValues$(this.selectedAccountUID, this.selectedOrganisationUID, asset.AssetID, dateStart, dateEnd).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				// filter((dataPointValues) => !!dataPointValues),
				tap(() => {
				}),
				finalize(() => {
					console.log("Entered Finalise")
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
					this.displaySpinner(false);
					if (scrollableDiv) {
						scrollableDiv.scrollLeft = scrollX;
						scrollableDiv.scrollTop = scrollY;
					}
				})
			).subscribe({
				next: (dataPointValues: any) => {
					console.log("Entered Next")
					try {
						let parsedData: ISensorData[];

						parsedData = dataPointValues.map((dataPointValue: any) => {
							return {
								//AssetId: dataPointValue.AssetID,
								AssetId: asset.AssetID,
								timestamp: dataPointValue.Timestamp,
								sampleDate: new Date(dataPointValue.Timestamp),
								...dataPointValue.Values
							}
						});
						console.log("Parsed DataPoints")

						this.parsedData = parsedData;

						if (this.viewing || this.editing) {
							this.existingDistValues = parsedData.filter((x: ISensorData) => x.timestamp >= this.existingDistribution.StartPoint
								&& x.timestamp <= this.existingDistribution.EndPoint);
						}

						// Extract unique pairs of Si and Pv
						let uniqueDPPairs = [...new Set(this.filteredDataPoints.map((x: DataPoint) => `${x.DataPointID},${x.SI},${x.EngMin},${x.EngMax}`))];
						// console.log(this.dataPoints)
						// let uniqueSIPairs = [...new Set(this.dataPoints.map((x: DataPoint) => `${x.SI},${x.PrimaryIndex}`))];
						let uniqueSIPairs = [
							...new Set(
								this.filteredDataPoints
									.sort((a: DataPoint, b: DataPoint) => {
										const aPrimaryIndex = a.PrimaryIndex ? "true" : "false";
										const bPrimaryIndex = b.PrimaryIndex ? "true" : "false";
										return aPrimaryIndex.localeCompare(bPrimaryIndex) * -1;
									})
									.map((x: DataPoint) => `${x.SI},${x.PrimaryIndex}`)
							)
						];

						// let uniqueSIPairs = [...new Set(this.dataPoints.map((x: DataPoint) => `${x.SI}`))];

						// parse the unique pairs back into separate Datapoints
						let uniqueDP = uniqueDPPairs.map((pair: any) => {
							let [dp, si, EngMin, EngMax] = pair.split(",");
							return { Dp: dp, Si: si, Max: EngMax, Min: EngMin };
						});

						this.uniqueDPs = uniqueDP;

						// Calculate the Min and Max from the Actual Data based on the Datapoint						
						let calcMinMax = uniqueDP.map((DP: any, index) => {
							const values = parsedData
								.map(item => item[`[${DP.Dp}]`])
								.filter(value => typeof value === 'number') as number[];
							// Find the minimum value
							if (values.length != 0) {
								// This is commented out as it caused errors on long periods
								// DP.Min = Math.min(...values);
								// DP.Max = Math.max(...values);
								// This works around the issue
								DP.Min = values.reduce((min, val) => val < min ? val : min, values[0]);
								DP.Max = values.reduce((max, val) => val > max ? val : max, values[0]);
							} else {
								DP.Min = 0;
								DP.Max = 0;
							}
						});

						// parse the unique pairs back into separate Si and Pv values
						let uniqueSI = uniqueSIPairs.map((pair: any) => {
							let [si, primary] = pair.split(",");
							return { Si: si, Primary: primary };
						});

						// // parse the unique pairs back into separate Si and Pv values
						// let uniqueSI = uniqueSIPairs.map((pair: any) => {
						// 	let [si] = pair.split(",");
						// 	return { Si: si};
						// });

						this.myChart.primaryYAxis = {};

						this.myChart.axes = [];

						// Create axes for each unique unit
						let axes = uniqueSI.map((unit: any, index) => {

							// Calculate the Min and Max from the SI data in the chart
							let { min, max, interval } = this.findMinMaxForSi(uniqueDP, unit.Si);

							if (min == 0 && max == 1) {
								max = 2;
								interval = 1;
							}

							// Build Axis
							let axis: any = {
								// Assuming you want each axis to have a unique name based on the unit
								name: `Axis${unit.Si}`,
								// You can add more properties here if needed

								majorTickLines: { width: 0 },
								minorTickLines: { width: 0 },

								majorGridLines: { width: 0 },
								minorGridLines: { width: 0 },

								minimum: min,
								maximum: max, /** Let the Chart calculate the Max */

								interval: interval,//0.2,
								rowIndex: 1,
								lineStyle: { width: 1 },
								labelFormat: `{value} ${unit.Si}`,
							};

							// Set opposedPosition to true only for the none primary Value
							if (unit.Primary != 'true') {
								axis.opposedPosition = true;
								axis.rowIndex = 0;
							}
							return axis;
						});

						// this.myChart.primaryYAxis.visible=false;
						if (axes[0]) {
							axes[0].majorGridLines = { width: 1 };
							axes[0].opposedPosition = false;
							this.myChart.primaryYAxis = axes[0];

							//this.myChart.primaryXAxis.majorGridLines = { width: 0 };

							// Creating an array of the remaining elements of 'axes'
							let remainingAxes = axes.slice(1);
							// Assigning the remaining axes to the 'axes' property of 'myChart'
							this.myChart.axes = remainingAxes;
						}

						// Clear previous series
						this.myChart.clearSeries()

						// Add new Series
						this.filteredDataPoints.forEach((dPoint: AssetDataPoint) => {
							this.plotIfSelected(dPoint);
						});

						if (this.editing && this.firstRun) {
							this.firstRun = false;
							this.dialogForm.markAsPristine();
							this.dialogForm.markAsUntouched();
						}

						if (returnValues) {
							resolve(parsedData);
						} else {
							resolve(true);
						}
					} catch (error) {
						console.error(error);
						reject(true);
					}
				},
				error: (error) => {
					this.dataPointValues = [];
					this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getAssetDataPointValues$", false)
					reject(true);
				},
				//complete: () => { console.log("this.AssetsDataService.getAssetDataPointValues$.subscribe: Complete"); }
			});
		});
	}

	public numPlottedDatapoints: number = 0;

	/** plotIfSelected */
	plotIfSelected(dPoint: AssetDataPoint) {
		// Determine the color based on the content of dPoint.SI
		let seriesColor: string = 'red'; //default color 
		let existingColor: boolean = false;
		let findExistingColor = this.seriesColors.find((x) => x.datapointID == dPoint.DataPointID);
		if (findExistingColor && findExistingColor.color) {
			seriesColor = findExistingColor.color;
			existingColor = true;
		}
		if (!existingColor) {
			if (dPoint.SI.includes('g')) {
				// Check if lightGreenCyan has already been used
				if (this.seriesColors.some(s => s.color === this.lightGreenCyan)) {
					// If used, choose a darker shade
					seriesColor = this.seriesColors.some(s => s.color === this.lightGreenCyanDarker1)
						? this.lightGreenCyanDarker2
						: this.lightGreenCyanDarker1;
				} else {
					seriesColor = this.lightGreenCyan;
				}
			} else if (dPoint.SI.includes('C')) {
				// Check if darkBlue has already been used
				if (this.seriesColors.some(s => s.color === this.darkBlue)) {
					// If used, choose a darker shade
					seriesColor = this.seriesColors.some(s => s.color === this.darkBlueDarker1)
						? this.darkBlueDarker2
						: this.darkBlueDarker1;
				} else {
					seriesColor = this.darkBlue;
				}
			} else {
				// If no specific condition is met, use the next color in the default array
				seriesColor = this.defaultColors[this.defaultColorIndex];

				// Increment the defaultColorIndex, and loop back to 0 if we reach the end of the array
				this.defaultColorIndex = (this.defaultColorIndex + 1) % this.defaultColors.length;
			}
		}

		let seriesName = dPoint.SI == '' ? dPoint.DPTitle : `${dPoint.DPTitle} (${dPoint.SI})`
		this.myChart.addSeries([{
			animation: {
				enable: false
			},
			emptyPointSettings: { mode: "Drop" },
			type: 'Line',
			dataSource: this.parsedData,
			name: seriesName,
			xName: 'timestamp',
			yName: `[${dPoint.DataPointID}]`,
			marker: {
				visible: false,
				width: 8,
				height: 8,
				shape: 'Triangle',
				dataLabel: { "name": 'x' },
				isFilled: true
			},
			yAxisName: `Axis${dPoint.SI}`,
			fill: seriesColor
		}]);
		if (!existingColor) {
			this.seriesColors.push({
				datapointID: dPoint.DataPointID,
				color: seriesColor
			});
		}

		this.primaryMax = <number>this.myChart.primaryYAxis.maximum;

		if (this.myChart.axes[0]) {
			this.secondaryMax = <number>this.myChart.axes[0].maximum;
		}

		this.numPlottedDatapoints++;

		if ((this.viewing || this.editing) && this.numPlottedDatapoints == this.filteredDataPoints.length) {
			setTimeout(() => {
				this.showDistributionArea();
			}, 10);
		}
	}

	/**
	 * Show the Spinner
	 * @param show
	 */
	displaySpinner(show: boolean) {
		// this.globalFunctions.displaySpinner(show);
		this.spinner = show;
	}

	/**
	 * REturn the Axis scale Parameters
	 * @param data
	 * @param targetSi
	 * @returns
	 */
	findMinMaxForSi(data: any[], targetSi: string): { min: number | 0, max: number | 999, interval: number | 1 } {
		// Filter the array to include only objects where Si equals the targetSi
		const filtered = data.filter(item => item.Si.toLowerCase() === targetSi.toLowerCase());

		if (filtered.length === 0) {
			return { min: 0, max: 0, interval: 1 }; // No matching objects found
		}

		// Extract Min and Max values from the filtered objects
		const minValues = filtered.map(item => item.Min);
		const maxValues = filtered.map(item => item.Max);

		// Find the minimum and maximum values
		let min = Math.min(...minValues);
		let max = Math.max(...maxValues);

		min = Math.floor(min);
		max = Number(max.toFixed(1));
		let interval = ((max - min) / 13);
		if (interval > 1) {
			interval = Number(interval.toFixed(1));
			interval = Math.ceil(interval)
		} else { interval = this.roundToNearestTenth(interval); }

		max = max + interval;
		interval = (min == max) ? interval = 1 : interval

		return { min, max, interval };
	}

	/** Round to Nearest 10th */
	roundToNearestTenth(num: number): number {
		return Math.ceil(num * 10) / 10;
	}

	/**
	 * Get the Data Points for the Asset
	 * @param asset
	 * @param dateStart
	 * @param dateEnd
	 * @returns
	 */
	GetDataPoints(asset: LevelAsset) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.dataPoints = [];
		this.filteredDataPoints = [];
		this.assetsDataService.getAssetDataPoints$(this.selectedAccountUID, this.selectedOrganisationUID, asset.AssetID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((dataPoints) => !!dataPoints),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (dataPoints: any) => {
				try {
					this.dataPoints = dataPoints.filter((x: DataPoint) => x.Enabled == true);
					this.filteredDataPoints = this.onlyDisplayCharted ? this.dataPoints.filter((x: AssetDataPoint) => x.ShowOnChart) : this.dataPoints;

					if (this.viewing || this.editing) {
						let existingDPIDs: number[] = this.existingDistribution.DatapointIDs.split(",").map((x: string) => parseInt(x));
						if (this.filteredDataPoints.length != existingDPIDs.length) {
							this.onlyDisplayCharted = false;
							this.filteredDataPoints = this.dataPoints.filter((x: AssetDataPoint) => existingDPIDs.includes(x.DataPointID));
						} else {
							this.datapointBox.clear();
						}
					}

					this.dataPoints.forEach(datapoint => {
						datapoint.SI = datapoint.SI.replace('%', ''); /** otherwise the stripline selector will break everything */
					})
					this.GetDataPointValues(this.asset, this.startDate.getTime(), this.endDate.getTime());
				} catch (error) {
					this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getAssetDataPoints.next", false);
				}
			},
			error: (error) => {
				if (error.status != 404) {
					this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getAssetDataPoints$", false);
				}
			},
			//complete: () => { console.log("this.AssetsDataService.getAssetDataPoints$.subscribe: Complete"); }
		});
	}

	/**
	 * toggleAutomatic
	 * @param event event 
	 */
	toggleAutomatic(event: any) {
		this.automaticSelected = event.checked;
		if (event.checked) {
			this.dialogForm.get('automaticWindowWidth')?.enable();
			this.dialogForm.get('automaticWindowWidth')?.setValidators([Validators.required]);
			this.dialogForm.get('recreationFrequency')?.enable();
			this.dialogForm.get('recreationFrequency')?.setValidators([Validators.required]);
			this.dialogForm.get('overlapPeriod')?.enable();
			this.dialogForm.get('overlapPeriod')?.setValidators([Validators.required]);
			this.dialogForm.updateValueAndValidity();
			this.getAutomaticData();
		} else {
			this.dialogForm.get('automaticWindowWidth')?.disable();
			this.dialogForm.get('automaticWindowWidth')?.clearValidators();
			this.dialogForm.get('recreationFrequency')?.disable();
			this.dialogForm.get('recreationFrequency')?.clearValidators();
			this.dialogForm.get('overlapPeriod')?.disable();
			this.dialogForm.get('overlapPeriod')?.clearValidators();
			this.dialogForm.get('distributionData')?.setValidators([Validators.required]);
			this.dialogForm.get('isAutomatic')?.setValue(false);
			this.automaticSelected = false;
			this.dialogForm.updateValueAndValidity();
		}
	}

	/**
	 * onSelectionComplete
	 * @param event event 
	 */
	onSelectionComplete(event: any) {
		let val = event.selectedDataValues.
			map((x: any, index: number) => (
				{
					datapoint: this.dataPoints.
						find((x: AssetDataPoint) =>
							x.DataPointID == parseInt(this.myChart.series[index].yName!.replace("[", "").replace("]", ""))),
					values: x.map((y: any) => (
						{
							time: y.x.getTime(),
							value: y.y
						}))
				}
			));
		this.selectedDistributionData = val;
		this.dialogForm.get('distributionData')?.setValue(val);
		this.dialogForm.markAsDirty();
	}

	/**
	 * getDatapointIDFromSeriesValues
	 * @param values values  
	 * @returns 
	 */
	getDatapointIDFromSeriesValues(values: any[]): number {
		let result: number = 0;
		console.log(`Values length: ${values.length}`);
		if (this.myChart.series) {
			let start: number = values[0].x.getTime();
			let end: number = values[values.length - 1].x.getTime();
			let valuesAvg = (values.map((x: any) => x.y).reduce((sum: number, value: number) => sum + value, 0) / values.length);
			this.myChart.series.forEach((series: any) => {
				let filteredSeries = series.points.filter((x: any) => {
					if (((x.xValue >= (start)) && (x.xValue <= (end + 1000)))) {
						if (values.some((y: any) => {
							return (x.x.setMilliseconds(0) == y.x.setMilliseconds(0) && x.y == y.y)
						})) {
							return true;
						} else {
							return false;
						}
					} else {
						return false;
					}
				}).map((x: any) => x.y);
				console.log(`FilteredSeries length: ${filteredSeries.length}`);
				let seriesAvg = filteredSeries.reduce((sum: number, value: number) => sum + value, 0) / filteredSeries.length;
				if (seriesAvg == valuesAvg) {
					result = parseInt(series.rangeColorName.replace("[", "").replace("]", ""));
				}

				let bb = values;
				let arr1: { x: number, y: number }[] = values.map((x: any) => ({ x: x.x.getTime(), y: x.y }));
				let arr2: { x: number, y: number }[] = series.points.filter((x: any) => (x.xValue >= (start)) && (x.xValue <= (end + 1000))).map((x: any) => ({ x: x.xValue, y: x.y }));
				let diff1 = this.difference(arr1, arr2);
				let diff2 = this.difference(arr2, arr1);
			})
		}
		return result;
	}

	difference(a: any[], b: any[]): any[] {
		return a.filter(x => !b.some(y => x.x === y.x && x.y === y.y));
	}


	/**
	 * getTotalPoints
	 * @param data data 
	 */
	getTotalPoints(): number {
		if (this.manualSelected && this.selectedDistributionData && this.selectedDistributionData.length > 0) {
			return this.selectedDistributionData.reduce((sum, arr) => sum + arr.values.length, 0);
		} else if (!this.manualSelected && this.automaticData && this.automaticData.length > 0) {
			return this.automaticData.reduce((sum, arr) => sum + arr.values.length, 0);
		} else {
			return 0;
		}
	}

	/**
	 * getWidthInDays
	 * @param data data 
	 * @returns 
	 */
	getWidthInDays(): string {
		if (this.manualSelected && this.selectedDistributionData && this.selectedDistributionData.length > 0) {
			return (
				(this.selectedDistributionData[0].values[this.selectedDistributionData[0].values.length - 1].time
					- this.selectedDistributionData[0].values[0].time) / (1000 * 60 * 60 * 24)
			).toFixed(2);
		} else if (!this.manualSelected && this.automaticData && this.automaticData.length > 0) {
			return (
				(this.automaticData[0].values[this.automaticData[0].values.length - 1].time
					- this.automaticData[0].values[0].time) / (1000 * 60 * 60 * 24)
			).toFixed(2);
		} else {
			return "";
		}
	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/**
	 * filterShowOnChart
	 * @param filter filter 
	 */
	filterShowOnChart(filter: boolean) {
		this.onlyDisplayCharted = filter;
		if (filter) {
			if (this.datapointBox) {
				this.datapointBox.clear();
			}
			this.filteredDataPoints = this.dataPoints.filter((x: AssetDataPoint) => x.ShowOnChart);
			this.dialogForm.get('datapoints')?.disable();
		} else {
			// this.filteredDataPoints = this.dataPoints;			
			this.filteredDataPoints = [];
			this.dialogForm.get('datapoints')?.enable();
		}
		this.GetDataPointValues(this.asset, this.startDate.getTime(), this.endDate.getTime());
	}

	/**
	 * toggleRemind
	 * @param event event  
	 */
	toggleRemind(event: any) {
		this.remindSelected = event.checked;
		if (event.checked) {
			this.dialogForm.get('remindAfter')?.enable();
			this.dialogForm.get('remindAfter')?.setValidators([Validators.required]);
			this.dialogForm.get('remindAfter')?.updateValueAndValidity();
		} else {
			this.dialogForm.get('remindAfter')?.disable();
			this.dialogForm.get('remindAfter')?.clearValidators();
			this.dialogForm.get('remindAfter')?.updateValueAndValidity();
		}
	}

	/**
	 * getAutomaticData
	 * @returns 
	 */
	async getAutomaticData(changingValue: number = 0): Promise<void> {
		this.noOkay = true;
		let values: any[] = [];
		let noMap: boolean = false;
		let automaticWindowWidth: number = 0;
		if (changingValue) {
			automaticWindowWidth = changingValue;
		} else {
			automaticWindowWidth = this.dialogForm.get('automaticWindowWidth')?.value ?? 0;
		}
		if (automaticWindowWidth > 0) {
			let end = new Date().getTime();
			let start = end - (automaticWindowWidth * 24 * 60 * 60 * 1000);
			let shouldFetchValues: boolean = true;
			if (this.parsedData && this.parsedData.length > 0) {
				let minTimestamp = Math.min(...this.parsedData.map((x: ISensorData) => x.timestamp));
				let maxTimestamp = Math.max(...this.parsedData.map((x: ISensorData) => x.timestamp));
				if (minTimestamp <= start && maxTimestamp >= (end - 600000)) {
					shouldFetchValues = false;
					values = this.parsedData.filter((x: ISensorData) => x.timestamp >= start && x.timestamp <= end);
				} else if (this.automaticData && this.automaticData.length > 0) {
					let minTimestamp = this.automaticData[0].values[0].time;
					let maxTimestamp = this.automaticData[0].values[this.automaticData[0].values.length - 1].time;
					if (minTimestamp <= start && maxTimestamp >= (end - 600000)) {
						noMap = true;
						shouldFetchValues = false;
						this.automaticData.forEach((x: any) => {
							values.push({ datapoint: x.datapoint, values: x.values.filter((y: any) => y.time >= start && y.time <= end) })
						})
					}
				}
				if (shouldFetchValues) {
					// let result = await this.GetDataPointValuesAutomatic(this.asset, start, end);
					let result = await this.GetDataPointValues(this.asset, start, end, true);
					if (result && result.length > 0) {
						values = result;
					}
				}
			}
			this.startDate = new Date(start);
			this.endDate = new Date(end);
			this.visibleStartDate = this.startDate;
			this.visibleEndDate = this.endDate;
		}
		let distData: any = [];
		if (!noMap) {
			values.forEach((x: ISensorData) => {
				this.dataPoints.forEach((dp: AssetDataPoint) => {
					if (x.hasOwnProperty(`[${dp.DataPointID}]`)) {
						if (distData.length == 0 || !distData.find((y: any) => y.datapoint.DataPointID == dp.DataPointID)) {
							distData.push({
								datapoint: dp,
								values: []
							});
						}
						let valArr = distData.find((y: any) => y.datapoint.DataPointID == dp.DataPointID).values;
						valArr.push({
							time: x.timestamp,
							value: x[`[${dp.DataPointID}]`]
						});
					}
				})
			})
		} else {
			distData = values;
		}
		this.dialogForm.get('distributionData')?.setValue(distData);
		this.automaticData = distData;
		if (this.myChart) {
			this.myChart.primaryXAxis.stripLines = [];
		}
		this.showDistributionArea(this.automaticData[0].values[0].time, this.automaticData[0].values[this.automaticData[0].values.length - 1].time);
		this.noOkay = false;
	}

	/**
	 * GetDataPointValuesAutomatic
	 * @param asset asset 
	 * @param dateStart dateStart 
	 * @param dateEnd dateEnd 
	 * @returns 
	 */
	async GetDataPointValuesAutomatic(asset: LevelAsset, dateStart: any, dateEnd: any): Promise<ISensorData[]> {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.displaySpinner(true);
		return new Promise<ISensorData[]>((resolve, reject) => {
			this.assetsDataService.getAssetDataPointValues$(this.selectedAccountUID, this.selectedOrganisationUID, asset.AssetID, dateStart, dateEnd).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				// filter((dataPointValues) => !!dataPointValues),
				tap(() => {
				}),
				finalize(() => {
					console.log("Entered Finalise")
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
					this.displaySpinner(false);
				})
			).subscribe({
				next: (dataPointValues: any) => {
					try {
						let parsedData: ISensorData[]

						parsedData = dataPointValues.map((dataPointValue: any) => {
							return {
								//AssetId: dataPointValue.AssetID,
								AssetId: asset.AssetID,
								timestamp: dataPointValue.Timestamp,
								sampleDate: new Date(dataPointValue.Timestamp),
								...dataPointValue.Values
							}
						});

						resolve(parsedData);

					} catch (error) {
						console.error(error);
						reject();
					}
				},
				error: (error) => {
					this.dataPointValues = [];
					this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getAssetDataPointValues$", false);
					reject();
				}
			});
		})
	}

	/**
	 * getDistribution
	 * @param distID distID 
	 */
	getDistribution(distID: number) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.modelsService.getAssetDistribution$(this.selectedAccountUID, this.selectedOrganisationUID, 0, distID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((distribution) => !!distribution),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (distribution: any) => {
				this.existingDistribution = distribution[0];
			},
			error: (error) => {
				if (error.status != 404) {
					this.globalFunctions.HandleError(error, "Error: this.AssetsDataService.getAssetDistribution$", false);
				}
			},
			//complete: () => { console.log("this.AssetsDataService.getAssetDataPoints$.subscribe: Complete"); }
		});
	}

	/**
	 * showDistributionArea
	 * @returns 
	 */
	showDistributionArea(start?: number, end?: number) {
		if (this.myChart.primaryXAxis.stripLines ? this.myChart.primaryXAxis.stripLines.length : 0 > 0) {
			return;
		}
		if (this.myChart && this.existingDistribution) {
			let existingStripLines: StripLineSettingsModel[] = [];
			if (this.myChart.primaryXAxis.stripLines) {
				existingStripLines = this.myChart.primaryXAxis.stripLines;
			}

			existingStripLines.push(
				{
					startFromAxis: false,
					start: this.existingDistribution.StartPoint,
					end: this.existingDistribution.EndPoint,
					zIndex: 'Over',
					opacity: 1,
					color: '#29abe21a',
					border: { color: '#29abe2', width: 1.5, dashArray: '3,3' },
				}
			);
			this.myChart.primaryXAxis.stripLines = existingStripLines;
		} else if (this.myChart && start && end) {
			let existingStripLines: StripLineSettingsModel[] = [];
			if (this.myChart.primaryXAxis.stripLines) {
				existingStripLines = this.myChart.primaryXAxis.stripLines;
			}

			existingStripLines.push(
				{
					startFromAxis: false,
					start: start,
					end: end,
					zIndex: 'Over',
					opacity: 1,
					color: '#29abe21a',
					border: { color: '#29abe2', width: 1.5, dashArray: '3,3' },
				}
			);
			this.myChart.primaryXAxis.stripLines = existingStripLines;
		}
		setTimeout(() => {
			this.showDistributionArea();
		}, 1000);
	}

	/**
	 * selectEntireRange
	 */
	selectEntireRange() {
		if (this.myChart) {
			this.addEntireRangeStripline();

			let distData: any = [];

			let values: ISensorData[] = this.parsedData;

			values.forEach((x: ISensorData) => {
				this.dataPoints.forEach((dp: AssetDataPoint) => {
					if (x.hasOwnProperty(`[${dp.DataPointID}]`)) {
						if (distData.length == 0 || !distData.find((y: any) => y.datapoint.DataPointID == dp.DataPointID)) {
							distData.push({
								datapoint: dp,
								values: []
							});
						}
						let valArr = distData.find((y: any) => y.datapoint.DataPointID == dp.DataPointID).values;
						valArr.push({
							time: x.timestamp,
							value: x[`[${dp.DataPointID}]`]
						});
					}
				})
			})
			this.dialogForm.get('distributionData')?.setValue(distData);
			this.selectedDistributionData = distData;
			this.dialogForm.markAsDirty();
		}
	}

	/**
	 * getDistributionRange
	 * @returns 
	 */
	getDistributionRange(): string {
		if (this.manualSelected) {
			if (this.selectedDistributionData && this.selectedDistributionData.length > 0) {
				let start = this.selectedDistributionData[0].values[0].time;
				let end = this.selectedDistributionData[0].values[this.selectedDistributionData[0].values.length - 1].time;
				return `${this.getShortDate(new Date(start))} - ${this.getShortDate(new Date(end))}`;
			} else {
				return "";
			}
		}
		else {
			if (this.automaticData && this.automaticData.length > 0) {
				let start = this.automaticData[0].values[0].time;
				let end = this.automaticData[0].values[this.automaticData[0].values.length - 1].time;
				return `${this.getShortDate(new Date(start))} - ${this.getShortDate(new Date(end))}`;
			} else {
				return "";
			}
		}
	}

	/**
	 * addEntireRangeStripline
	 */
	addEntireRangeStripline() {
		if (this.myChart) {
			let existingStripLines: StripLineSettingsModel[] = [];
			if (this.myChart.primaryXAxis.stripLines) {
				existingStripLines = this.myChart.primaryXAxis.stripLines;
			}

			// have to add 2 striplines, one for the border and one for the fill 
			// because syncfusion does not support the border having a different opacity to the fill
			// existingStripLines.push(
			// 	{
			// 		startFromAxis: false,
			// 		start: this.parsedData[0].timestamp,
			// 		end: this.parsedData[this.parsedData.length - 1].timestamp,
			// 		zIndex: 'Over',
			// 		opacity: 1,
			// 		color: 'none',
			// 		border: { color: '#29abe2', width: 1.5, dashArray: '3,3' },
			// 	}
			// );

			existingStripLines.push(
				{
					startFromAxis: false,
					start: this.parsedData[0].timestamp,
					end: this.parsedData[this.parsedData.length - 1].timestamp,
					zIndex: 'Over',
					opacity: 1,
					color: '#29abe21a',
					border: { color: '#29abe2', width: 1.5, dashArray: '3,3' },
				}
			);
			this.myChart.primaryXAxis.stripLines = existingStripLines;
		}
	}

	/**
	 * openTestDistributionWindow
	 */
	openTestDistributionWindow() {
		let data = {
			Asset: this.asset,
			Distribution: this.existingDistribution
		}

		const dialogRef = this.dialog.open(TestDistributionComponent
			, { data: data }
		);

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
		});
	}

	/**
	 * changeSelection
	 * @param isManual isManual  
	 */
	changeSelection(isManual: boolean) {
		this.dialogForm.markAsPristine();
		this.manualSelected = isManual;
		this.selectedDistributionData = [];
		this.automaticData = [];
		this.dialogForm.get('distributionData')?.setValue([]);
		if (this.myChart) {
			this.myChart.primaryXAxis.stripLines = [];
		}
		if (this.manualSelected) {
			this.strDuration = "00:12:00";
			let parts: any[] = this.strDuration.split(":");
			this.endDate = new Date();
			this.startDate = new Date();
			this.startDate.setDate(this.startDate.getDate() - parts[0]);
			this.startDate.setHours(this.startDate.getHours() - parts[1]);
			this.startDate.setMinutes(this.startDate.getMinutes() - parts[2]);
			this.visibleStartDate = this.startDate;
			this.visibleEndDate = this.endDate;
			this.GetDataPointValues(this.asset, this.startDate.getTime(), this.endDate.getTime());
			this.dialogForm.get('automaticWindowWidth')?.disable();
			this.dialogForm.get('automaticWindowWidth')?.clearValidators();
			this.dialogForm.get('distributionData')?.setValidators([Validators.required]);
			this.dialogForm.get('isAutomatic')?.setValue(false);
			this.automaticSelected = false;
			this.dialogForm.updateValueAndValidity();
			this.myChart.selectionMode = 'DragX';
		} else {
			this.strDuration = "custom";
			this.myChart.selectionMode = 'None';
		}
		this.globalFunctions.DisplaySnackBar({
			message: `Selection changed to ${isManual ? 'Manual' : 'Automatic'}`,
			duration: 2000,
			action: ""
		})
	}

	/** recreateModel */
	recreateModel() {
		this.dialogRef.close();

		let data = {
			asset: this.asset,
			viewing: false,
			editing: true,
			distributionID: this.existingDistribution.AssetDistributionID,
			existingDistribution: this.existingDistribution
		}

		const dialogRef = this.dialog.open(CreateAssetDistributionComponent
			, { data: data }
		);

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			this.patchDistribution(result.tolerance, result.sensitivity, result.recreationFrequency, result.overlapPeriod);
		});
	}

	/**
	 * patchDistribution
	 * @param tolerance tolerance 
	 * @param sensitivity sensitivity 
	 * @param recreationFrequency recreationFrequency 
	 * @param overlapPeriod overlapPeriod 
	 */
	patchDistribution(tolerance: number, sensitivity: number, recreationFrequency: number, overlapPeriod: number) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.modelsService.patchDistribution$(this.selectedAccountUID, this.selectedOrganisationUID, this.existingDistribution.AssetDistributionID,
			tolerance, sensitivity, recreationFrequency, overlapPeriod).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (result) => {
					this.globalFunctions.DisplaySnackBar({
						message: "Asset Model Updated",
						type: SnackType.Info,
						duration: 1000
					});
					this.siteSubscriptions.UpdateModelList(true);
					this.siteSubscriptions.RefreshModelEdits(true);
				},
				error: (error) => { this.globalFunctions.HandleError(error, "Error: this.assetsDataService.postAssetDistribution$", false); },
			});
	}

	/**
	 * datapointSelected
	 * @param event 
	 */
	datapointSelected(event: any) {
		if (!this.viewing && !this.editing) {
			this.filteredDataPoints = [];
			event.value.forEach((datapointID: number) => {
				let dp = this.dataPoints.find((x: AssetDataPoint) => x.DataPointID == datapointID);
				if (dp) {
					this.filteredDataPoints.push(dp);
				}
			})
			this.GetDataPointValues(this.asset, this.startDate.getTime(), this.endDate.getTime());
		}
	}
}

